import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  getDayOne,
  getSocActions,
  getSocNeedsYou,
  getSocPeople,
  getSocSources,
  type DayOneReport,
  type SocAction,
  type SocActions,
  type SocDarkWebFinding,
  type SocNeedsYou,
  type SocPeople,
  type SocSources,
  type SocToday,
} from '../../lib/api'
import { plainDay } from '../../lib/businessTime'
import { CouldNotSee, card, h2Class, kicker, linkClass, routeStatus, when } from './SocShell'
import { SocQuestionCard } from './SocQuestion'
import { TellSocForm } from './TellSoc'
import { cn } from '../../lib/utils'

// The owner's home (docs/design/customer-page/Main.dc.html): today's read,
// the day-one check (BRIEF-B19),
// Needs you, what the SOC did, your people, the four sources, and the note
// box. Each section is one /soc/ route and says that route's could_not_see.
//
// BRIEF-F3, support view: staff see this same page for one business, fed by
// /hub/console/businesses/{id}/as-owner/* (the owner's own routes, called
// server-side with an owner-scope view). With `support` set the page is
// read-only: no answer, note or tripwire control is drawn, no link leads to
// the customer's own settings, and the day-one check (not one of those
// routes) is not fetched.

export interface SocLoaders {
  needsYou: () => Promise<SocNeedsYou>
  actions: (days: number) => Promise<SocActions>
  people: () => Promise<SocPeople>
  sources: () => Promise<SocSources>
}

// Called through, not referenced, at import: a page that imports this file
// for a helper need not provide every loader.
const OWNER_LOADERS: SocLoaders = {
  needsYou: () => getSocNeedsYou(),
  actions: (days) => getSocActions(days),
  people: () => getSocPeople(),
  sources: () => getSocSources(),
}

export interface SupportView {
  /** The business's name, for the banner. */
  business: string
  loaders: SocLoaders
}

export const supportBanner = (business: string) =>
  `Viewing ${business}'s page as the owner sees it. Read-only.`

export type Loaded<T> = { data: T | null; error: boolean }

export function useSocRoute<T>(load: () => Promise<T>): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ data: null, error: false })
  useEffect(() => {
    let cancelled = false
    load().then(
      (data) => !cancelled && setState({ data, error: false }),
      () => !cancelled && setState({ data: null, error: true }),
    )
    return () => {
      cancelled = true
    }
    // Fetch once per mount; the loaders are module functions.
  }, [])
  return state
}

export function SectionState({ state, what }: { state: Loaded<unknown>; what: string }) {
  if (state.error) {
    return (
      <p className="m-0 text-[15px] text-[#6b5a3e]" role="alert">
        We could not load {what} just now, so this section shows nothing rather than a guess.
      </p>
    )
  }
  if (state.data === null) {
    return (
      <p className="m-0 text-[15px] text-[#6b5a3e]" role="status">
        Loading {what}…
      </p>
    )
  }
  return null
}

export function readoutParagraphs(readout: string): string[] {
  return readout
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
}

// E3c: when /soc/today says the read is not current, the route's own
// sentence ("The daily read has not run ...") is lifted out of could_not_see
// into one banner above the headline, so it is not said twice. The page never
// works out the age itself.
const STALE_READ = 'The daily read has not run'

export function staleRead(today: SocToday): { banner: string | null; couldNotSee: string[] } {
  if (today.read_is_current !== false) return { banner: null, couldNotSee: today.could_not_see }
  const banner = today.could_not_see.find((line) => line.startsWith(STALE_READ)) ?? null
  return { banner, couldNotSee: today.could_not_see.filter((line) => line !== banner) }
}

export function StaleReadBanner({ sentence }: { sentence: string | null }) {
  if (!sentence) return null
  return (
    <p
      role="note"
      data-testid="stale-read"
      className="m-0 rounded-[10px] border border-brass bg-[#f6efe2] px-5 py-3.5 text-[16px] leading-snug text-navy"
    >
      {sentence}
    </p>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-[15px]">
      <span>{label}</span>
      <strong className="text-right">{children}</strong>
    </div>
  )
}

// ── 0. A tripwire fired ─────────────────────────────────────────────

// D5b: /soc/today carries the open fires for the owner only; a member's
// /soc/today has neither field, so this card is never drawn for them.
export interface TripwireFired {
  name: string
  where: string | null
  fired_at: string | null
  line: string
}

// D8: an escalation we texted the owner about twice and have not heard
// back on. Owner only, like the tripwire fires.
export interface OwnerNotReached {
  person: string
  texted_at: string[]
  line: string
}

// D12: a wake we could not text because the owner has no mobile we may
// text (none, not verified, or STOP). Owner only; null once it is verified.
export interface OwnerCannotText {
  at: string
  reason: string
  line: string
}

export type OwnerToday = SocToday & {
  tripwires_fired_open?: number
  tripwires_fired?: TripwireFired[]
  owner_not_reached?: OwnerNotReached[]
  owner_cannot_text?: OwnerCannotText | null
}

export function OwnerCannotTextCard({ today, readOnly = false }: { today: OwnerToday; readOnly?: boolean }) {
  const held = today.owner_cannot_text
  if (!held) return null
  return (
    <section
      aria-label="We could not text you"
      data-testid="owner-cannot-text"
      className="flex min-w-0 flex-col gap-2.5 rounded-[10px] border-2 border-brass bg-[#f6efe2] px-5 py-4 text-navy"
    >
      <p className={cn(kicker, 'm-0')}>We could not text you</p>
      <p className="m-0 break-words font-serif text-[20px] leading-snug sm:text-[24px]">{held.line}</p>
      {/* A STOP stands even when the phone is verified again: only the owner
          replying START lifts it, so that case has no Settings link. */}
      {!readOnly && held.reason !== 'refused:phone_stopped' && (
        <Link to="/client/settings/escalation#mobile" className={cn(linkClass, 'self-start')}>
          Verify your mobile
        </Link>
      )}
    </section>
  )
}

export function OwnerNotReachedCard({ today }: { today: OwnerToday }) {
  const items = today.owner_not_reached ?? []
  if (items.length === 0) return null
  return (
    <section
      aria-label="We could not reach you"
      data-testid="owner-not-reached"
      className="flex min-w-0 flex-col gap-2.5 rounded-[10px] border-2 border-brass bg-[#f6efe2] px-5 py-4 text-navy"
    >
      <p className={cn(kicker, 'm-0')}>We could not reach you</p>
      {items.map((n, i) => (
        <p key={`${n.person}-${n.texted_at[0] ?? i}`} className="m-0 break-words font-serif text-[20px] leading-snug sm:text-[24px]">
          {n.line}
        </p>
      ))}
    </section>
  )
}

export function TripwireFiredCard({ today, readOnly = false }: { today: OwnerToday; readOnly?: boolean }) {
  const fires = today.tripwires_fired ?? []
  if (!today.tripwires_fired_open || today.tripwires_fired_open <= 0) return null
  return (
    <section
      aria-label="A tripwire fired"
      data-testid="tripwire-fired"
      className="flex min-w-0 flex-col gap-2.5 rounded-[10px] border-2 border-brass bg-[#f6efe2] px-5 py-4 text-navy"
    >
      <p className={cn(kicker, 'm-0')}>A tripwire fired</p>
      {fires.map((f, i) => (
        <div key={`${f.name}-${f.fired_at ?? i}`} className="min-w-0">
          <p className="m-0 break-words font-serif text-[20px] leading-snug sm:text-[24px]">{f.line}</p>
          <p className="m-0 mt-1 break-words text-[15px] text-[#6b5a3e]">
            {[f.where, when(f.fired_at, 'HH:mm, EEE d MMM')].filter(Boolean).join(' · ')}
          </p>
        </div>
      ))}
      {!readOnly && (
        <Link to="/client#sources" className={cn(linkClass, 'text-[15px]')}>
          See your tripwires
        </Link>
      )}
    </section>
  )
}

// ── 1. Today ────────────────────────────────────────────────────────

function TodaySection({ today, readOnly }: { today: SocToday; readOnly: boolean }) {
  const [headline, ...rest] = readoutParagraphs(today.readout)
  const day = today.day ? plainDay(today.day, 'EEEE d MMMM') : when(today.day_start, 'EEEE d MMMM')
  const readAt = when(today.read_at, 'HH:mm, EEE d MMM')
  const stale = staleRead(today)
  return (
    <section id="today" aria-label="Your SOC today" className="flex flex-wrap items-start gap-7">
      {stale.banner && (
        <div className="basis-full">
          <StaleReadBanner sentence={stale.banner} />
        </div>
      )}
      <div className="min-w-0 flex-[999_1_560px]">
        <p className={cn(kicker, 'm-0 mb-2.5')}>
          {['Your SOC', today.soc_team_name, day].filter(Boolean).join(' · ')}
        </p>
        <h1 className="m-0 mb-4 font-serif text-[30px] font-normal leading-tight sm:text-[40px] whitespace-pre-line">
          {headline}
        </h1>
        {rest.map((p, i) => (
          <p key={i} className="m-0 mb-3.5 text-lg leading-relaxed text-[#2b3540] whitespace-pre-line">
            {p}
          </p>
        ))}
        {today.accounts_not_read.length > 0 && (
          <ul className="m-0 mb-3.5 list-none p-0 text-[15px] text-[#6b5a3e]" aria-label="Not read">
            {today.accounts_not_read.map((n) => (
              <li key={n.person}>
                {n.person}: {n.reason}
              </li>
            ))}
          </ul>
        )}
        <CouldNotSee items={stale.couldNotSee} className="text-[15px]" />
      </div>
      <aside className={cn(card, 'flex flex-[1_1_300px] flex-col gap-4 px-6 py-5')} aria-label="Right now">
        <p className={cn(kicker, 'm-0')}>Right now</p>
        <Row label="Read">
          {today.accounts_read.length > 0 ? today.accounts_read.join(', ') : 'Nobody yet'}
        </Row>
        {today.accounts_not_read.length > 0 && (
          <Row label="Not read">{today.accounts_not_read.map((n) => n.person).join(', ')}</Row>
        )}
        <Row label="Last read">{readAt ?? 'No read recorded'}</Row>
        <Row label="Open with you">
          {today.open_questions_count === 0 ? 'Nothing' : `${today.open_questions_count} to answer`}
        </Row>
        <Row label="Response">
          <span className="text-[#8a6830]">{today.response_armed ? 'Switched on' : 'Switched off'}</span>
        </Row>
        {!readOnly && (
          <Link to="/client/settings/escalation" className={cn(linkClass, 'text-[15px]')}>
            Change how we reach you
          </Link>
        )}
      </aside>
    </section>
  )
}

// ── 2. Your day-one check ───────────────────────────────────────────

// BRIEF-B19: GET /day-one is the owner's (or admin's) day-one report. A 404
// means a plain member, another business, or no audit yet: the section is
// not drawn at all. While loading it draws nothing either, so a member or a
// business with no audit never sees it flash past.

type DayOneState =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'error' }
  | { kind: 'ready'; data: DayOneReport }

export function useDayOne(enabled = true): DayOneState {
  const [state, setState] = useState<DayOneState>({ kind: enabled ? 'loading' : 'hidden' })
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    getDayOne().then(
      (data) => !cancelled && setState({ kind: 'ready', data }),
      (err) => {
        if (cancelled) return
        setState(routeStatus(err) === 404 ? { kind: 'hidden' } : { kind: 'error' })
      },
    )
    return () => {
      cancelled = true
    }
  }, [enabled])
  return state
}

/** Customer text without our internal ruling numbers (e.g. "(ruling 19)"),
 * whichever field it arrives in: the reason, the report or a found item. */
export function withoutInternalTags(text: string | null | undefined): string {
  return (text ?? '').replace(/\s*\(ruling \d+\)/gi, '')
}

/** The route's reason as one plain sentence, without the internal ruling number. */
export function plainReason(reason: string | null): string {
  const why = withoutInternalTags(reason).trim().replace(/\.+$/, '')
  if (!why) return 'The reason was not recorded.'
  return `${why[0].toUpperCase()}${why.slice(1)}.`
}

function stillRunning(d: DayOneReport): boolean {
  return d.status === 'queued' || d.status === 'running' || d.report_status === 'in_progress'
}

export function DayOneSection({ state }: { state: DayOneState }) {
  if (state.kind === 'loading' || state.kind === 'hidden') return null
  if (state.kind === 'error') {
    return (
      <section id="day-one" aria-label="Your day-one check" className="flex flex-col gap-3.5">
        <h2 className={h2Class}>Your day-one check</h2>
        <p className="m-0 text-[15px] text-[#6b5a3e]" role="alert">
          We could not load your day-one check just now, so this section shows nothing rather than a guess.
        </p>
      </section>
    )
  }
  const d = state.data
  if (!d.report) {
    if (!stillRunning(d)) return null
    return (
      <section id="day-one" aria-label="Your day-one check" className="flex flex-col gap-3.5">
        <h2 className={h2Class}>Your day-one check</h2>
        <p className="m-0 text-[15px] text-[#6b5a3e]" role="status">
          Your first check is still running.
        </p>
      </section>
    )
  }
  const written = when(d.written_at ?? d.finished_at, 'EEE d MMM, HH:mm')
  const ready = d.qualified === true
  return (
    <section id="day-one" aria-label="Your day-one check" className="flex flex-col gap-3.5">
      <h2 className={h2Class}>Your day-one check</h2>
      <div
        data-testid="day-one-ready"
        className={cn(
          'rounded-[10px] border px-5 py-3.5 text-navy',
          ready ? 'border-[#e6dfd2] bg-white' : 'border-brass bg-[#f6efe2]',
        )}
      >
        <p className={cn(kicker, 'm-0 mb-1')}>Ready to be watched</p>
        <p className="m-0 font-serif text-[22px] leading-snug">
          {ready ? 'Yes, your business is ready to be watched.' : 'Not yet.'}
        </p>
        {!ready && (
          <p className="m-0 mt-1 text-[15px] text-[#2b3540]" data-testid="day-one-reason">
            {plainReason(d.qualified_reason)}
          </p>
        )}
      </div>
      {d.already_wrong.length > 0 && (
        <div className={cn(card, 'flex flex-col gap-2.5 px-5 py-4')} data-testid="day-one-wrong">
          <p className={cn(kicker, 'm-0')}>What we found already wrong</p>
          <ul className="m-0 list-none p-0 text-[15px] leading-snug text-[#2b3540]">
            {d.already_wrong.map((w) => (
              <li key={w.key} className="mb-2.5">
                <strong>{withoutInternalTags(w.what)}</strong>
                <span className="block">What to do: {withoutInternalTags(w.what_to_do)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <details className={cn(card, 'px-5 py-4')} open>
        <summary className={cn(linkClass, 'cursor-pointer text-[15px]')}>
          The full report{written ? `, written ${written}` : ''}
        </summary>
        <p
          className="m-0 mt-3 text-[15px] leading-relaxed text-[#2b3540] whitespace-pre-line"
          data-testid="day-one-report"
        >
          {withoutInternalTags(d.report)}
        </p>
      </details>
    </section>
  )
}

// ── 3. Needs you ────────────────────────────────────────────────────

function NeedsYouSection({ state, readOnly }: { state: Loaded<SocNeedsYou>; readOnly: boolean }) {
  return (
    <section id="needs-you" aria-label="Needs you" className="flex flex-col gap-3.5">
      <h2 className={h2Class}>Needs you</h2>
      <SectionState state={state} what="the open questions" />
      {state.data && state.data.items.length === 0 && (
        <p className="m-0 text-[15px] text-[#6b5a3e]">Nothing is waiting on you.</p>
      )}
      {state.data?.items.map((item) => (
        <SocQuestionCard key={item.ref} item={item} canAnswer={!readOnly && item.answerable !== false} />
      ))}
      <CouldNotSee items={state.data?.could_not_see} />
    </section>
  )
}

// ── 4. What your SOC did ────────────────────────────────────────────

function didWords(a: SocAction): string {
  switch (a.status) {
    case 'executed':
      return a.verified ? 'Done, and checked afterwards.' : 'Done; not yet checked afterwards.'
    case 'proposed_not_armed':
      // Never past tense, never the platform's state (BRIEF-D9): the route
      // sends the would-form of the step for a proposal.
      return 'Not done: response is switched off for your business.'
    case 'refused':
      return `Not done${a.reason ? ` (${a.reason.replace(/_/g, ' ')})` : ''}.`
    case 'nothing_to_do':
      return 'Nothing needed doing.'
    default:
      return `Tried; it did not complete${a.reason ? ` (${a.reason.replace(/_/g, ' ')})` : ''}.`
  }
}

function actionWords(a: SocAction): string {
  return a.status === 'proposed_not_armed' ? `We would ${a.action_words}.` : `${a.action_words}.`
}

function ActionsSection({ state }: { state: Loaded<SocActions> }) {
  const data = state.data
  return (
    <section aria-label="What your SOC did" className="flex flex-col gap-3.5">
      <h2 className={h2Class}>What your SOC did this week</h2>
      <SectionState state={state} what="what your SOC did" />
      {data && data.items.length === 0 && (
        <p className="m-0 text-[15px] text-[#6b5a3e]">
          Your SOC took no action in the last {data.days} days.
        </p>
      )}
      {data && data.items.length > 0 && (
        // BRIEF-E5d: on a phone each action is one stacked row (what, when,
        // outcome), so nothing needs a sideways swipe. The table is for wider
        // screens only.
        <ol
          className={cn(card, 'm-0 flex list-none flex-col p-0 sm:hidden')}
          aria-label="What your SOC did"
          data-testid="actions-list"
        >
          {data.items.map((a, i) => (
            <li
              key={`${a.when}-${i}`}
              className="flex min-w-0 flex-col gap-1 border-t border-[#efe9de] px-4 py-3.5 text-[15px] leading-snug first:border-t-0"
            >
              <p className="m-0 break-words">
                <strong>{a.person}.</strong> {a.found ?? 'What was found is not recorded.'}
              </p>
              <p className="m-0 text-sm text-[#6b5a3e]">{when(a.when) ?? 'Time not recorded'}</p>
              <p className="m-0 break-words">
                {actionWords(a)} {didWords(a)} {a.reversible ? 'Reversible.' : 'Not reversible.'}
              </p>
            </li>
          ))}
        </ol>
      )}
      {data && data.items.length > 0 && (
        <div className={cn(card, 'hidden overflow-x-auto px-4 pb-2.5 pt-1.5 sm:block sm:px-6')} data-testid="actions-table">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[.12em] text-[#6b5a3e]">
                <th className="px-2.5 py-3 font-normal">When</th>
                <th className="px-2.5 py-3 font-normal">What we found</th>
                <th className="px-2.5 py-3 font-normal">What we did</th>
                <th className="px-2.5 py-3 font-normal">Reversible</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((a, i) => (
                <tr key={`${a.when}-${i}`} className="border-t border-[#efe9de] align-top text-[15px]">
                  <td className="px-2.5 py-3 whitespace-nowrap">{when(a.when) ?? 'Time not recorded'}</td>
                  <td className="px-2.5 py-3">
                    <strong>{a.person}.</strong> {a.found ?? 'What was found is not recorded.'}
                  </td>
                  <td className="px-2.5 py-3">
                    {actionWords(a)} {didWords(a)}
                  </td>
                  <td className="px-2.5 py-3">{a.reversible ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CouldNotSee items={data?.could_not_see} />
      <p className="m-0 text-sm text-[#6b5a3e]">
        Your SOC acts only with four reversible steps: end sessions, disable a mail rule, revoke an
        app's access, hold an account. It never deletes mail, resets a password or removes a person.
      </p>
    </section>
  )
}

// ── 5. Your people ──────────────────────────────────────────────────

function PeopleSection({ state, readOnly }: { state: Loaded<SocPeople>; readOnly: boolean }) {
  return (
    <section id="people" aria-label="Your people" className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className={h2Class}>Your people</h2>
        {!readOnly && (
          <a href="#talk" className={cn(linkClass, 'text-[15px]')}>
            Tell your SOC something
          </a>
        )}
      </div>
      <SectionState state={state} what="your people" />
      {state.data && state.data.items.length === 0 && (
        <p className="m-0 text-[15px] text-[#6b5a3e]">No watched person is recorded for this business.</p>
      )}
      {state.data && state.data.items.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="people-grid">
          {state.data.items.map((p) => {
            const read = when(p.last_read)
            const marks = [
              p.open_question && 'Question open',
              p.close_watch && 'Close watch',
              read ? `Read ${read}` : 'Not read yet',
            ].filter(Boolean)
            const role = [p.org_role, ...p.role_facts].filter(Boolean).join(' · ')
            return (
              <div key={p.account} className={cn(card, 'flex flex-col gap-2 px-5 py-4')}>
                <div className="flex items-baseline justify-between gap-2">
                  <strong className="text-[17px]">{p.person}</strong>
                  <span className="text-right text-[13px] text-[#6b5a3e]">{marks.join(' · ')}</span>
                </div>
                {role && <span className="text-sm text-[#6b5a3e]">{role}</span>}
                {p.what_the_soc_knows.length > 0 ? (
                  <ul className="m-0 list-none p-0 text-[15px] leading-snug text-[#2b3540]">
                    {p.what_the_soc_knows.map((k, i) => (
                      <li key={i} className="mb-1">
                        {k}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="m-0 text-[15px] text-[#6b5a3e]">Your SOC has nothing recorded about {p.person} yet.</p>
                )}
              </div>
            )
          })}
        </div>
      )}
      <CouldNotSee items={state.data?.could_not_see} />
    </section>
  )
}

// ── 6. What we watch ────────────────────────────────────────────────

const sourceCard = cn(card, 'flex flex-col gap-2.5 border-t-[3px] border-t-brass p-5')

// E4: the heading names the business's own cloud. BRIEF-E5d: the name is
// the route's own `cloud_name` (E5b), never a code word worked out here.
// One cloud across every account names it; none, a mix, or a cloud the
// route does not name gets the neutral line.
export function cloudName(sources: SocSources | null): string | null {
  const names = new Set((sources?.accounts ?? []).map((a) => a.cloud_name ?? null))
  const only = names.size === 1 ? [...names][0] : null
  return only || null
}

/** Where the business keeps its files, in its cloud's own words. */
export function filesPlace(sources: SocSources | null): string | null {
  switch (cloudName(sources)) {
    case 'Microsoft 365':
      return 'OneDrive'
    case 'Google Workspace':
      return 'Google Drive'
    default:
      return null
  }
}

export function watchHeading(sources: SocSources | null): string {
  const name = cloudName(sources)
  return name
    ? `What we watch, beyond what ${name} shows you`
    : 'What we watch, beyond what your email provider shows you'
}

// BRIEF-E5d (E5b's findings): each dark-web finding is one line in the
// owner's words with its next step. Never a count by kind.
export function DarkWebFindings({
  findings,
  intro,
}: {
  findings: SocDarkWebFinding[] | undefined
  intro: string
}) {
  const items = findings ?? []
  if (items.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5" data-testid="dark-web-findings">
      <p className="m-0 text-[15px] text-[#2b3540]">{intro}:</p>
      <ul className="m-0 list-none p-0 text-[15px] leading-snug text-[#2b3540]">
        {items.map((f, i) => (
          <li key={`${f.kind}-${f.found_on}-${i}`} className="mb-2 break-words">
            <span className="block">{f.line}.</span>
            <span className="block text-[#6b5a3e]" data-testid="finding-next-step">
              Next step: {f.next_step}.
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SourcesSection({ state, readOnly = false }: { state: Loaded<SocSources>; readOnly?: boolean }) {
  const s = state.data
  const cannotSee = s ? Array.from(new Set(s.accounts.flatMap((a) => a.cannot_see))) : []
  return (
    <section id="sources" aria-label="What we watch" className="flex flex-col gap-3.5">
      <h2 className={h2Class}>{watchHeading(s)}</h2>
      <SectionState state={state} what="what we watch" />
      {s && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className={sourceCard}>
            <p className={cn(kicker, 'm-0')}>Your accounts</p>
            {s.accounts.length === 0 && (
              <p className="m-0 text-[15px] text-[#2b3540]">No connected account is recorded.</p>
            )}
            <ul className="m-0 list-none p-0 text-[15px] leading-snug text-[#2b3540]">
              {s.accounts.map((a) => (
                <li key={a.account} className="mb-1">
                  <strong>{a.person}</strong>
                  {a.cloud_name ? ` (${a.cloud_name})` : ''}:{' '}
                  {when(a.last_successful_poll)
                    ? `last read ${when(a.last_successful_poll)}`
                    : 'never read successfully'}
                </li>
              ))}
            </ul>
            {cannotSee.length > 0 && (
              <p className="m-0 text-[13px] text-[#6b5a3e]">Cannot see: {cannotSee.join('; ')}.</p>
            )}
          </div>

          <div className={sourceCard}>
            <p className={cn(kicker, 'm-0')}>Daily threat intelligence</p>
            <strong className="text-[17px]">
              {s.threat_intelligence.brief_date
                ? s.threat_intelligence.is_today
                  ? "Today's brief, applied to you"
                  : `The brief of ${s.threat_intelligence.brief_date}`
                : 'No brief'}
            </strong>
            {s.threat_intelligence.brief_date && (
              <p className="m-0 text-[15px] text-[#2b3540]" data-testid="intel-brief-date">
                Brief date: {s.threat_intelligence.brief_date}
              </p>
            )}
            {s.threat_intelligence.applied_hits && (
              <p className="m-0 text-[15px] text-[#2b3540]" data-testid="intel-applied-hits">
                Matched against your team's activity in the last {s.threat_intelligence.applied_hits.days} days:{' '}
                <strong>{s.threat_intelligence.applied_hits.count}</strong>, across{' '}
                <strong>{s.threat_intelligence.applied_hits.people}</strong> of your accounts
              </p>
            )}
            {s.threat_intelligence.headline && (
              <p className="m-0 text-[15px] leading-snug text-[#2b3540]">{s.threat_intelligence.headline}</p>
            )}
            {s.threat_intelligence.applied.length > 0 && (
              <ul className="m-0 list-none p-0 text-sm text-[#2b3540]">
                {s.threat_intelligence.applied.map((i, n) => (
                  <li key={n} className="mb-1">
                    {i.title}
                  </li>
                ))}
              </ul>
            )}
            <CouldNotSee
              items={s.could_not_see.filter((line) => line.startsWith('threat intelligence'))}
              className="text-[13px]"
            />
          </div>

          <div className={sourceCard}>
            <p className={cn(kicker, 'm-0')}>Dark-web watch</p>
            {s.dark_web.domains.length === 0 && (
              <p className="m-0 text-[15px] text-[#2b3540]">No mail domain is recorded.</p>
            )}
            <ul className="m-0 list-none p-0 text-[15px] leading-snug text-[#2b3540]">
              {s.dark_web.domains.map((d) => (
                <li key={d.domain} className="mb-1">
                  <strong>{d.domain}</strong>: {d.result}
                  {when(d.last_checked, 'EEE d MMM') ? `, ${when(d.last_checked, 'EEE d MMM')}` : ''}
                </li>
              ))}
            </ul>
            <DarkWebFindings
              findings={s.dark_web.findings}
              intro={`Found in the last ${s.dark_web.lookback_days} days`}
            />
            <p className="m-0 text-[13px] text-[#6b5a3e]">
              A clean check means nothing was found, not that nothing exists.
            </p>
          </div>

          <div className={sourceCard}>
            <p className={cn(kicker, 'm-0')}>Tripwires</p>
            {s.tripwires.length === 0 && (
              <p className="m-0 text-[15px] text-[#2b3540]">No tripwire is placed.</p>
            )}
            <ul className="m-0 list-none p-0 text-[15px] leading-snug text-[#2b3540]">
              {s.tripwires.map((t) => (
                <li key={t.id} className="mb-1">
                  <strong>{t.name ?? 'A tripwire'}</strong>
                  {t.where ? ` in ${t.where}` : ''}: {t.status}
                </li>
              ))}
            </ul>
            {!readOnly && (
              <Link to="/client/tripwires" className={cn(linkClass, 'text-sm')}>
                Place another tripwire
              </Link>
            )}
          </div>
        </div>
      )}
      <CouldNotSee items={s?.could_not_see} />
    </section>
  )
}

// ── 7. Tell your SOC ────────────────────────────────────────────────

// BRIEF-E5d: the same example for every business: no name, no place.
export const NOTE_PLACEHOLDER = 'Someone new starts next Monday and will need an account.'

function TalkSection({ socName, readOnly }: { socName: string; readOnly: boolean }) {
  return (
    <section
      id="talk"
      aria-label="Talk to your SOC"
      className="flex flex-wrap items-start gap-5 rounded-[10px] border border-navy bg-navy p-6 text-cream"
    >
      <div className="min-w-0 flex-[999_1_420px]">
        <h2 className="m-0 mb-2 font-serif text-[26px] font-normal">Tell {socName} something</h2>
        <p className="m-0 text-base leading-normal text-[#d9cdb8]">
          A new starter, someone leaving, a trip, a new accountant with access, a change of phone.
          Anything you tell us becomes part of how we read your business.
        </p>
      </div>
      {readOnly ? (
        <p className="m-0 flex-[1_1_360px] text-base text-[#d9cdb8]" data-testid="support-no-note">
          The owner's note box. Read-only here: only the owner sends a note.
        </p>
      ) : (
        <TellSocForm placeholder={NOTE_PLACEHOLDER} />
      )}
    </section>
  )
}

export function OwnerHome({ today, support }: { today: OwnerToday; support?: SupportView }) {
  const readOnly = support !== undefined
  const loaders = support?.loaders ?? OWNER_LOADERS
  const dayOne = useDayOne(!readOnly)
  const needsYou = useSocRoute(loaders.needsYou)
  const actions = useSocRoute(() => loaders.actions(7))
  const people = useSocRoute(loaders.people)
  const sources = useSocRoute(loaders.sources)
  return (
    <>
      <TripwireFiredCard today={today} readOnly={readOnly} />
      <OwnerNotReachedCard today={today} />
      <OwnerCannotTextCard today={today} readOnly={readOnly} />
      <TodaySection today={today} readOnly={readOnly} />
      <DayOneSection state={dayOne} />
      <NeedsYouSection state={needsYou} readOnly={readOnly} />
      <ActionsSection state={actions} />
      <PeopleSection state={people} readOnly={readOnly} />
      <SourcesSection state={sources} readOnly={readOnly} />
      <TalkSection socName={today.soc_team_name} readOnly={readOnly} />
    </>
  )
}
